/**
 * One segmented look for every "choose one of a few" control in the dashboard:
 * Tabs-based segmented tabs (resource and settings forms) and the
 * `SegmentedControl` / `SegmentedNav` switches (Dag · Vecka · Månad,
 * Medarbetare · Kund, Lista · Filter).
 *
 * - Track: a recessed muted surface with a visible border; segments sit
 *   inside it.
 * - Segment: button-shaped — pointer cursor, muted text, a hover surface and a
 *   focus-visible ring.
 * - Selected: a raised thumb (card surface, border, soft shadow, semibold
 *   foreground text). Never the primary action fill: a mode switch must not
 *   compete with the surface's one primary action (design system, "Command
 *   hierarchy" rule 1).
 *
 * The selected outline is a border, not a ring, so the focus-visible ring
 * stays visible on the selected segment. Tailwind only sees literal class
 * names, so the selected treatment is spelled once per state attribute below;
 * `segmented-tabs-styles.test.ts` proves both spellings carry the same tokens.
 *
 * @example
 * ```tsx
 * <TabsList className={segmentedTabsListClassName}>
 *   <TabsTrigger className={segmentedTabsTriggerClassName} value="a">…</TabsTrigger>
 * </TabsList>
 * ```
 *
 * @example Grid layout (equal columns)
 * ```tsx
 * <TabsList className={cn("grid w-full grid-cols-2 mb-6", segmentedTabsBarClassName)}>
 *   <TabsTrigger className={segmentedTabsTriggerClassName} value="a">…</TabsTrigger>
 * </TabsList>
 * ```
 */

/** The recessed track every segmented control sits on. */
export const segmentedTrackSurfaceClassName =
  "rounded-lg border border-border bg-muted";

/** A segment before selection: button-shaped, hoverable, focusable. */
export const segmentedSegmentBaseClassName = [
  "inline-flex min-w-0 cursor-pointer select-none items-center justify-center",
  "whitespace-nowrap rounded-md border border-transparent font-medium",
  "text-muted-foreground",
  "transition-[color,background-color,border-color,box-shadow] duration-150",
  "hover:bg-card/70 hover:text-foreground",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  "focus-visible:ring-offset-1 focus-visible:ring-offset-background",
  "disabled:pointer-events-none disabled:opacity-50",
  "aria-disabled:pointer-events-none aria-disabled:opacity-50",
].join(" ");

/**
 * The raised thumb for `SegmentedControl` / `SegmentedNav` items, which carry
 * `data-selected="true"` whatever their semantics (radio, pressed or link).
 */
export const segmentedSelectedClassName = [
  "data-[selected=true]:border-border data-[selected=true]:bg-card",
  "data-[selected=true]:font-semibold data-[selected=true]:text-foreground",
  "data-[selected=true]:shadow-sm data-[selected=true]:hover:bg-card",
].join(" ");

/** The same raised thumb for Radix `TabsTrigger` (`data-state="active"`). */
export const segmentedTabsActiveClassName = [
  "data-[state=active]:border-border data-[state=active]:bg-card",
  "data-[state=active]:font-semibold data-[state=active]:text-foreground",
  "data-[state=active]:shadow-sm data-[state=active]:hover:bg-card",
  // `TabsTrigger` draws its own active ring. The thumb's outline is its
  // border, and a data-state ring would sit on top of the focus ring (data
  // variants follow focus-visible in the stylesheet), so drop that ring and
  // restore the focus ring for a focused active tab.
  "data-[state=active]:ring-0",
  "data-[state=active]:focus-visible:ring-2 data-[state=active]:focus-visible:ring-ring",
  // `TabsTrigger`'s own inactive hover is the track colour; keep the lift.
  "data-[state=inactive]:hover:bg-card/70",
].join(" ");

/** Padding and track behind triggers; use with flex or grid `TabsList`. */
export const segmentedTabsBarClassName = [
  segmentedTrackSurfaceClassName,
  "p-1 gap-1",
].join(" ");

/** Default flex-wrap row for many tabs (resource forms, settings). */
export const segmentedTabsListClassName = [
  "flex h-auto min-h-10 w-full flex-wrap justify-start",
  segmentedTabsBarClassName,
].join(" ");

/** A form/settings tab: the shared segment at form size, raised when active. */
export const segmentedTabsTriggerClassName = [
  segmentedSegmentBaseClassName,
  "gap-2 px-3 py-2 text-xs sm:text-sm",
  segmentedTabsActiveClassName,
].join(" ");
